import * as crypto from 'crypto';
import base64url from 'base64url';
import * as cbor from 'cbor';
import MemoryAdapter from './memory-adapter.service';
import AttestationChallengeBuilder, { AttestationChallenge } from './attestation-challenge-builder.service';
import AssertionChallengeBuilder, { AssertionChallenge } from './assertion-challenge-builder.service';
import Dictionaries from './dictionaries.service';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LoggingService } from '../logging.service';

export interface UserCredential {
  id?: string;
  name: string;
  username?: string;
  password?: string;
  authenticator?: {
    fmt: string;
    publicKey: string;
    counter: number;
    credID: string | Buffer;
  };
}

export interface StatusResponse {
  status: string;
  message?: string;
}

export interface AuthenticatorAttestationResponse {
  verified: boolean;
  authrInfo: {
    fmt: string;
    publicKey: string;
    counter: number;
    credID: string;
  };
}

export interface AuthenticatorAssertionResponse {
  verified: boolean;
  counter: number;
}

interface MakeCredAuthData {
  rpIdHash: string | Uint8Array;
  flagsBuf: string | Uint8Array;
  flags: number;
  counter: number;
  counterBuf: Uint8Array;
  aaguid: string | Uint8Array;
  credID: string | Uint8Array;
  COSEPublicKey: string | Uint8Array;
}

interface AssertAuthData {
  rpIdHash: number[];
  flagsBuf: number[];
  flags: number;
  counter: number;
  counterBuf: Buffer;
}

@Injectable({
  providedIn: 'root',
})
export default class WebAuthnService {
  constructor(
    private readonly dictionaries: Dictionaries,
    private readonly attestationBuilder: AttestationChallengeBuilder,
    private readonly assertionBuilder: AssertionChallengeBuilder,
    private readonly memoryAdapter: MemoryAdapter,
    private readonly logger: LoggingService,
  ) {
    this.inMemoryStore = memoryAdapter;

    const attestationOptions = Object.values(this.dictionaries.AttestationConveyancePreference);
    if (!attestationOptions.includes(this.dictionaries.AttestationConveyancePreference.NONE)) {
      throw new Error(`Invalid attestation value. Must be "none".`);
    }
  }

  private get store(): MemoryAdapter {
    return this.inMemoryStore;
  }

  public async register(user: UserCredential): Promise<AttestationChallenge> {
    if (!user) {
      throw new Error('user is mandatory');
    }

    const username = user.username;
    if (!username) {
      throw new Error('bad request');
    }

    user.id = base64url(crypto.randomBytes(32).toString('hex'));

    const existing = this.store.getUser(username) as UserCredential;
    if (existing?.authenticator) {
      user = existing;
    }

    delete user.password;
    this.store.putUser(username, user);

    const attestation = this.attestationBuilder
      .setUserInfo(user)
      .setAttestationType('none')
      .setAuthenticator({
        authenticatorAttachment: 'platform',
        userVerification: 'required',
      })
      .setRelyingPartyInfo({ name: 'ViridisTerra' || user.username })
      .build({ status: 'ok' });

    attestation.session = { challenge: attestation.challenge, name: username };

    return attestation as AttestationChallenge;
  }

  public async login(username: string): Promise<AssertionChallenge> {
    if (!username) {
      throw new Error('user required');
    }

    const user = this.store.getUser(username);
    if (!user) {
      throw new Error('user does not exist');
    }

    const assertion = this.assertionBuilder
      .addAllowedCredential(base64url.encode(user.authenticator?.credID.toString('hex') ?? ''))
      .setUserVerification('required')
      .build({ status: 'ok' });
    assertion.session = { challenge: assertion.challenge, name: username };

    return assertion;
  }

  public async response(
    credResponse: {
      rawId: string;
      response: {
        attestationObject: string;
        clientDataJSON: string;
        authenticatorData?: string;
        signature?: string;
      };
      authenticatorAttachment: string;
      id: string;
      type: string;
    },
    session: { challenge: string | Buffer | undefined; name: string } | undefined,
  ): Promise<StatusResponse> {
    if (!credResponse) {
      throw new Error('bad bequest');
    }

    const { id, rawId, response, type } = credResponse;

    if (!id || !rawId || !response || !type) {
      throw new Error('response missing one or more of id/rawId/response/type fields');
    }
    if (type !== 'public-key') {
      throw new Error("response type must be 'public-key'");
    }
    if (!session) {
      throw new Error('session is undefined');
    }

    let clientData;
    try {
      const json = base64url.decode(response.clientDataJSON);
      clientData = JSON.parse(json);
    } catch (err) {
      throw new Error('failed to decode client data');
    }

    const { challenge, origin } = clientData;

    if (!challenge || challenge !== base64url.decode(session.challenge as string)) {
      throw new Error('mismatched challenge');
    }

    if (!origin || (origin !== environment.origin && origin !== 'http://localhost:4200')) {
      throw new Error('mismatched origin');
    }

    const username = session.name;
    if (!username) {
      throw new Error(`mismatched username`);
    }

    let result: AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;
    const user = this.store.getUser(username);

    try {
      if (response.attestationObject !== undefined) {
        result = this.verifyAuthenticatorAttestationResponse(response);

        if (result.verified && user) {
          user.authenticator = result.authrInfo;
          this.store.putUser(username, user);
          this.store.putValue('biometric', 'true');
        }
      } else if (response.authenticatorData !== undefined && user) {
        result = this.verifyAuthenticatorAssertionResponse(
          response as {
            authenticatorData: string;
            clientDataJSON: string;
            signature: string;
          },
          user.authenticator,
        );

        if (result.verified && user.authenticator && result.counter) {
          if (result.counter <= user.authenticator.counter && result.counter !== 0) {
            throw new Error('Auth counter did not increase!');
          }

          user.authenticator.counter = result.counter ?? 0;

          this.store.putUser(username, user);
          this.store.putValue('biometric', 'true');
        }
      } else {
        throw new Error('failed to determine response type');
      }
    } catch (err) {
      throw new Error('failed to authenticate');
    }

    if (result.verified) {
      return { status: 'ok' };
    } else {
      throw new Error('verification failed');
    }
  }

  private verifyAuthenticatorAttestationResponse(webauthnResponse: {
    attestationObject: string;
    clientDataJSON: string;
  }): AuthenticatorAttestationResponse {
    const attestationBuffer = base64url.toBuffer(webauthnResponse.attestationObject);
    const ctapMakeCredResp = cbor.decodeAllSync(attestationBuffer)[0];
    const authrDataStruct = this.parseMakeCredAuthData(ctapMakeCredResp.authData);
    if (!(authrDataStruct.flags & 0x01)) {
      throw new Error('User was NOT presented during authentication!');
    }

    const publicKey = this.COSEECDHAtoPKCS(authrDataStruct.COSEPublicKey);
    const response: Partial<AuthenticatorAttestationResponse> = { verified: false };
    if (ctapMakeCredResp.fmt === this.dictionaries.AttestationConveyancePreference.NONE) {
      response.verified = true;
    } else {
      throw new Error(`Unsupported attestation format: ${ctapMakeCredResp.fmt}`);
    }

    if (response.verified) {
      response.authrInfo = {
        fmt: 'none',
        publicKey: base64url.encode(publicKey),
        counter: authrDataStruct.counter,
        credID: base64url.encode(authrDataStruct.credID as string),
      };
    }

    return response as AuthenticatorAttestationResponse;
  }

  private verifyAuthenticatorAssertionResponse(
    webauthnResponse: {
      authenticatorData: string;
      clientDataJSON: string;
      signature: string;
    },
    authr?: { fmt: string; publicKey: string; counter: number; credID: string | Buffer },
  ): AuthenticatorAssertionResponse {
    const authenticatorData = base64url.toBuffer(webauthnResponse.authenticatorData);

    const response: Partial<AuthenticatorAssertionResponse> = { verified: false };

    if (authr?.fmt === 'none') {
      const authrDataStruct = this.parseGetAssertAuthData(authenticatorData as unknown as number[]);
      if (!(authrDataStruct.flags & 0x01)) {
        throw new Error('User was not presented durring authentication!');
      }

      const clientDataHash = this.hash(base64url.toBuffer(webauthnResponse.clientDataJSON));
      const signatureBase = Buffer.concat([authenticatorData, clientDataHash]);
      const publicKey = this.ASN1toPEM(base64url.toBuffer(authr.publicKey));
      const signature = base64url.toBuffer(webauthnResponse.signature);

      response.counter = authrDataStruct.counter;
      response.verified = this.verifySignature(signature, signatureBase, publicKey);
    }

    return response as AuthenticatorAssertionResponse;
  }

  private hash(data: Buffer): Buffer {
    return crypto.createHash('sha256').update(data).digest();
  }

  private verifySignature(signature: Buffer, data: Buffer, publicKey: string): boolean {
    return crypto.createVerify('sha256').update(data).verify(publicKey, signature);
  }

  private parseGetAssertAuthData(buffer: number[]): AssertAuthData {
    const rpIdHash = buffer.slice(0, 32);
    buffer = buffer.slice(32);

    const flagsBuf = buffer.slice(0, 1);
    buffer = buffer.slice(1);

    const flags = flagsBuf[0];

    const counterBuf = buffer.slice(0, 4) as unknown as Buffer;
    buffer = buffer.slice(4);

    const counter = counterBuf.readUInt32BE(0);

    return { rpIdHash, flagsBuf, flags, counter, counterBuf };
  }

  private parseMakeCredAuthData(buffer: Uint8Array): MakeCredAuthData {
    const rpIdHash = buffer.slice(0, 32);
    buffer = buffer.slice(32);

    const flagsBuf = buffer.slice(0, 1);
    buffer = buffer.slice(1);

    const flags = flagsBuf[0];

    const counterBuf = buffer.slice(0, 4) as unknown as Buffer;
    buffer = buffer.slice(4);

    const counter = counterBuf.readUInt32BE(0);

    const aaguid = buffer.slice(0, 16);
    buffer = buffer.slice(16);

    const credIDLenBuf = buffer.slice(0, 2) as unknown as Buffer;
    buffer = buffer.slice(2);

    const credIDLen = credIDLenBuf.readUInt16BE(0);

    const credID = buffer.slice(0, credIDLen);
    buffer = buffer.slice(credIDLen);

    const COSEPublicKey = buffer;

    return {
      rpIdHash,
      flagsBuf,
      flags,
      counter,
      counterBuf,
      aaguid,
      credID,
      COSEPublicKey,
    };
  }

  private COSEECDHAtoPKCS(COSEPublicKey: string | Uint8Array): Buffer {
    const coseStruct = cbor.decodeAllSync(COSEPublicKey)[0];
    const tag = Buffer.from([0x04]);
    const x = coseStruct.get(-2);
    const y = coseStruct.get(-3);

    return Buffer.concat([tag, x, y]);
  }

  private ASN1toPEM(pkBuffer: Buffer): string {
    if (!Buffer.isBuffer(pkBuffer)) {
      throw new Error('ASN1toPEM: pkBuffer must be Buffer.');
    }

    let type;
    if (pkBuffer.length == 65 && pkBuffer[0] == 0x04) {
      pkBuffer = Buffer.concat([Buffer.from('3059301306072a8648ce3d020106082a8648ce3d030107034200', 'hex'), pkBuffer]);

      type = 'PUBLIC KEY';
    } else {
      type = 'CERTIFICATE';
    }

    const b64cert = pkBuffer.toString('base64');

    let PEMKey = '';
    for (let i = 0; i < Math.ceil(b64cert.length / 64); i++) {
      const start = 64 * i;
      PEMKey += b64cert.substr(start, 64) + '\n';
    }

    PEMKey = `-----BEGIN ${type}-----\n` + PEMKey + `-----END ${type}-----\n`;
    return PEMKey;
  }

  private readonly inMemoryStore!: MemoryAdapter;
}
